/**
 * App load-registry — the "how do I mount this appId" lookup, populated from
 * the backend app tiles (`/portalapps/api/v1/Apps/GetTiles`).
 *
 * This is the React-shell equivalent of legacy `resolveIframeOverride`
 * (raptor-shell-ui `mfe-container.component.ts`): the launcher tile for each
 * app carries an `appTypeID` (how it loads) and an `externalRoutePath` (the URL
 * for iframe/external apps), and the shell mounts the app accordingly. By
 * reusing the SAME tile feed the legacy shell uses, every app opens here the
 * way it opens in the old shell:
 *
 *   - appTypeID 1 (MFEApp)      → inline Custom Element (legacy loader)
 *   - appTypeID 3 (IframeApp)   → embedded <iframe> at its own document
 *   - appTypeID 4 (ExternalApp) → opened in a new tab (handled in the UI)
 *
 * Keyed by the FINAL segment of `tileRoutePath`, lowercased — the same value
 * the MFE host routes to (`/app/{appId}`) and legacy matches on
 * (`tileRoutePath.split('/').pop()`).
 */

// Mirror of legacy `app-types.ts` (shell-header). Only the values the shell
// branches on are listed.
export const APP_TYPE_MFE = 1;
export const APP_TYPE_IFRAME = 3;
export const APP_TYPE_EXTERNAL = 4;

export interface AppLoadMeta {
  /** Final segment of the tile route path, lowercased (the mount appId). */
  readonly appId: string;
  /** Legacy `appTypeID` — drives inline vs iframe vs external mounting. */
  readonly appTypeID: number;
  /** Embedded URL for iframe/external apps (legacy `externalRoutePath`). */
  readonly externalRoutePath?: string;
}

const registry = new Map<string, AppLoadMeta>();

/** Derive the mount appId (final route segment, lowercased) from a tile path. */
export function appIdFromRoutePath(tileRoutePath: string): string {
  const segments = (tileRoutePath || '').split('/').filter(Boolean);
  return (segments[segments.length - 1] ?? '').toLowerCase();
}

/**
 * Replace the registry with the supplied entries. Called whenever the app-tile
 * query resolves (per client). Idempotent — a re-fetch simply overwrites.
 */
export function setAppRegistry(entries: readonly AppLoadMeta[]): void {
  registry.clear();
  for (const entry of entries) {
    if (entry.appId) registry.set(entry.appId, entry);
  }
}

/**
 * Fold additional entries into the registry WITHOUT clearing it, and without
 * overwriting an appId that is already present. Used to add the admin tile feed
 * on top of the workspace registry so an admin app resolves its correct loader
 * type (iframe / MF / external) while a same-named workspace app keeps priority.
 */
export function mergeAppRegistry(entries: readonly AppLoadMeta[]): void {
  for (const entry of entries) {
    if (entry.appId && !registry.has(entry.appId)) registry.set(entry.appId, entry);
  }
}

export function clearAppRegistry(): void {
  registry.clear();
}

/** Look up how a given appId should be mounted, or `undefined` if unknown. */
export function getAppLoadMeta(appId: string): AppLoadMeta | undefined {
  return registry.get(appId.toLowerCase());
}

/** True once the registry has been populated at least once (tiles loaded). */
export function isAppRegistryReady(): boolean {
  return registry.size > 0;
}
